import { SidebarIconButton } from '@/components/sidebar/tabs/sidebar-icon-button.component';
import { iconBoxIsometric, iconBoxOpen } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { usePostHog } from 'posthog-js/react';
import { useState } from 'react';
import { useWhatsNew } from './use-whats-new.hook';

/**
 * The sidebar footer's What's new button, beside Help and support, as Notion
 * has it: a closed gold box that rattles now and then while an announcement
 * is unseen, popping open once the card is closed over it. It opens or closes
 * the card, at any time. Hidden while there's nothing to announce.
 */
export function WhatsNewButton({
  collapsed = false,
  iconSize,
}: {
  collapsed?: boolean;
  /** The glyph's size, where the sidebar sets one (see SidebarIconButton). */
  iconSize?: number;
}) {
  const { hasAny, hasUnread, visible, open, close } = useWhatsNew();
  const posthog = usePostHog();
  // Pops open only when it turns read in front of the user, not on load.
  const [wasUnread, setWasUnread] = useState(hasUnread);
  const [justOpened, setJustOpened] = useState(false);
  if (wasUnread !== hasUnread) {
    setWasUnread(hasUnread);
    setJustOpened(!hasUnread);
  }
  if (!hasAny) return null;
  return (
    <SidebarIconButton
      label={hasUnread ? "What's new, unread updates" : "What's new"}
      tooltip="What's new"
      icon={hasUnread ? iconBoxIsometric : iconBoxOpen}
      side={collapsed ? 'right' : 'top'}
      iconSize={iconSize}
      className={cn(
        'duration-300',
        collapsed && 'w-10',
        hasUnread
          ? 'whats-new-shake whats-new-shine text-[#E5AE18] dark:text-[#F2BC1B]'
          : justOpened && 'whats-new-opened'
      )}
      onClick={() => {
        posthog?.capture('whats_new_button_clicked', {
          unread: hasUnread,
          action: visible ? 'close' : 'open',
        });
        if (visible) close();
        else open();
      }}
    />
  );
}
